import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useActiveAccount } from 'thirdweb/react';
import { z } from 'zod';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import useFantomStarterAdminApi from '../../../hooks/useFantomStarterAdminApi';
import useDocusignApi from '../../../hooks/useDocusign';
import { 
  toast, 
  notifySuccess, 
  notifyError, 
  notifyInfo, 
  notifyWarning,
  NotificationPriority 
} from '../../../utils/notifications';

import { DesktopLayout } from '../../../components/Layouts';
import { arrowLeft, sparkles } from '../../../assets';
import { editorStateToFsParagraphs } from '../../../utils/editor';
import { formatNumberWithCommas } from '../../../utils/conversion';
import TextEditor from '../../../components/TextEditor/TextEditor';
import ImageUpload from '../../../components/Form/ImageUpload';
import FileDropImport from '../../../components/Form/FileDropImport';
import { setStateValue, STATE_KEYS } from '../../../utils/stateManagement';

// Form field types
type OfferingType = 'public' | 'private' | 'auction';

interface FormState {
  // Import section
  importUrl: string;
  
  // Fund Details section
  fundLogo: File | null;
  fundName: string;
  mainGraphic: File | null;
  tagline: string;
  overview: any; // This will be the TextEditor content
  
  // Investment Terms section
  tokenPrice: string;
  minInvestment: string;
  targetRaise: string;
  
  // Offering Structure section
  offeringType: OfferingType | null;
  accreditedOnly: boolean;
  
  // Timeline section
  launchDate: string | null;
  closingDate: string | null;
  
  // Agreement section
  agreementDoc: File | null;
}

// URLs for uploaded files
interface UploadedUrls {
  fundLogoUrl: string | null;
  mainGraphicUrl: string | null;
}

const CreateFund: React.FC = () => {
  const navigate = useNavigate();
  const activeAccount = useActiveAccount();
  const fantomStarterAdminApi = useFantomStarterAdminApi();
  const { createProject, saveProjectSummary, updateProjectDetails } = fantomStarterAdminApi.admin;
  const { createTemplate, getTemplateEditViewUrl } = useDocusignApi();
  
  // Refs for date inputs and section tracking
  const launchDateInputRef = useRef<HTMLInputElement>(null);
  const closingDateInputRef = useRef<HTMLInputElement>(null);
  const sectionRefs = useRef<Array<React.RefObject<HTMLElement>>>([
    React.createRef<HTMLElement>(),
    React.createRef<HTMLElement>(),
    React.createRef<HTMLElement>(),
    React.createRef<HTMLElement>(),
    React.createRef<HTMLElement>(),
  ]);
  const [activeSection, setActiveSection] = useState<number>(0);

  // Set up scroll observer to track active section
  useEffect(() => {
    const handleScroll = () => {
      // Using 160px offset to align with the scrollToSection function's 80px offset
      // Plus additional space to trigger section change at the right time
      const scrollPosition = window.scrollY + 160; 
      
      sectionRefs.current.forEach((sectionRef, index) => {
        if (sectionRef.current) {
          const sectionTop = sectionRef.current.offsetTop;
          const sectionHeight = sectionRef.current.offsetHeight;
          
          if (
            scrollPosition >= sectionTop &&
            scrollPosition < sectionTop + sectionHeight
          ) {
            setActiveSection(index);
          }
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Get company name from local storage
  const companyName = localStorage.getItem(`${activeAccount?.address}-companyName`) || null;
  
  // Function to scroll to section
  const scrollToSection = (index: number) => {
    if (sectionRefs.current[index]?.current) {
      // Adjust the offset to account for the navbar but without the breadcrumb
      // Using a smaller offset (80px) since we've removed the breadcrumb navigation
      window.scrollTo({
        top: sectionRefs.current[index].current!.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  // Form validation schema
  const schema = z.object({
    fundName: z.string().min(1, 'Fund name is required'),
    tagline: z.string().optional(),
    fundLogo: z.custom<File | null>(
      (val) => val instanceof File || val === null,
      { message: 'Please upload a fund logo' }
    ).refine(val => val !== null, 'Fund logo is required'),
    mainGraphic: z.custom<File | null>(
      (val) => val instanceof File || val === null,
      { message: 'Please upload a main fund graphic' }
    ).optional(),
    overview: z.object({
      paragraphs: z.array(z.any()).min(1, 'Fund overview must contain at least one paragraph')
    }),
    tokenPrice: z.string().min(1, 'Share price is required'),
    minInvestment: z.string().min(1, 'Minimum investment is required'),
    targetRaise: z.string().min(1, 'Target raise is required'),
    offeringType: z.enum(['public', 'private', 'auction']),
    launchDate: z.string().min(1, 'Launch date is required'),
    closingDate: z.string().min(1, 'Closing date is required'),
    agreementDoc: z.custom<File | null>((file) => file instanceof File, {
      message: 'Please upload an agreement document',
    }).refine(val => val !== null, 'Agreement document is required'),
  });

  // Form state
  const [formState, setFormState] = useState<FormState>({
    importUrl: '',
    fundLogo: null,
    fundName: '',
    mainGraphic: null,
    tagline: '',
    overview: { paragraphs: [] },
    tokenPrice: '',
    minInvestment: '',
    targetRaise: '',
    offeringType: null,
    accreditedOnly: false,
    launchDate: null,
    closingDate: null,
    agreementDoc: null,
  });
  
  // Uploaded file URLs
  const [uploadedUrls, setUploadedUrls] = useState<UploadedUrls>({
    fundLogoUrl: null,
    mainGraphicUrl: null,
  });
  
  // Form validation states
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validFields, setValidFields] = useState<Record<string, boolean>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [formStatus, setFormStatus] = useState<'idle' | 'valid' | 'error' | 'saving'>('idle');
  
  // Loading states
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isFetchingAI, setIsFetchingAI] = useState<boolean>(false);
  const [isImportingFile, setIsImportingFile] = useState<boolean>(false);
  
  // Validate a single field
  const validateField = (name: string, value: any): string | null => {
    switch (name) {
      case 'fundName':
        return !value ? 'Fund name is required' : null;
      case 'tokenPrice':
        return !value || isNaN(Number(value)) || Number(value) <= 0 
          ? 'Valid share price is required' 
          : null;
      case 'minInvestment':
        return !value || isNaN(Number(value)) || Number(value) <= 0 
          ? 'Valid minimum investment is required' 
          : null;
      case 'targetRaise':
        return !value || isNaN(Number(value)) || Number(value) <= 0 
          ? 'Valid target raise is required' 
          : null;
      case 'launchDate':
        return !value ? 'Launch date is required' : null;
      case 'closingDate':
        if (!value) return 'Closing date is required';
        if (formState.launchDate && new Date(value) <= new Date(formState.launchDate)) {
          return 'Closing date must be after launch date';
        }
        return null;
      default:
        return null;
    }
  };

  
  // Enhanced event handlers for form inputs with improved validation and feedback
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const inputEl = e.target;
    
    // For currency fields, we need to handle numeric input but display formatted
    if (['tokenPrice', 'targetRaise'].includes(name) && e.target instanceof HTMLInputElement) {
      // For input fields, we want to keep the raw numeric value in the state
      // But display it formatted with commas
      const numericValue = value.replace(/,/g, '');
      setFormState(prev => ({ ...prev, [name]: numericValue }));
      
      // For display purposes, we'll format the input value if it's a valid number
      if (numericValue && !isNaN(Number(numericValue))) {
        if (e.target.type === 'number') {
          // For number inputs we keep the raw value, since browsers handle formatting differently
          e.target.value = numericValue;
        }
      }
    } else {
      // Regular inputs without formatting
      setFormState(prev => ({ ...prev, [name]: value }));
    }
    
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Validate the field
    const errorMessage = validateField(name, value);
    if (errorMessage) {
      setErrors(prev => ({ ...prev, [name]: errorMessage }));
      setValidFields(prev => ({ ...prev, [name]: false }));
      
      // Add visual feedback for error
      inputEl.classList.remove('border-green-500', 'animate-popIn');
      
      // If this is a new error (field was previously valid), show animation
      if (validFields[name]) {
        inputEl.classList.add('animate-shake');
        setTimeout(() => {
          inputEl.classList.remove('animate-shake');
        }, 500);
      }
    } else {
      // Clear error if field is now valid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      
      // If field was previously invalid and is now valid, show success animation
      if (!validFields[name] && value) {
        inputEl.classList.add('animate-popIn', 'border-green-500');
        
        // Subtle bounce effect on the check icon
        const checkIcon = inputEl.parentElement?.querySelector('svg.text-green-500');
        if (checkIcon) {
          checkIcon.classList.add('animate-bounce');
          setTimeout(() => {
            checkIcon.classList.remove('animate-bounce');
          }, 500);
        }
      }
      
      setValidFields(prev => ({ ...prev, [name]: true }));
      
      // Provide subtle visual feedback for valid inputs
      if (name === 'importUrl' && !isFetchingAI && value) {
        // Highlight the extract button when URL is valid
        const extractButton = document.querySelector('[data-action="extract"]');
        if (extractButton) {
          extractButton.classList.add('animate-pulse', 'scale-105');
          setTimeout(() => {
            extractButton.classList.remove('animate-pulse', 'scale-105');
          }, 1000);
        }
      }
    }
  };
  
  // Handle focus events for enhanced UI feedback
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    const inputEl = e.target;
    
    // Add subtle visual cue for focused fields
    inputEl.classList.add('shadow-md', 'bg-white', 'border-[#6741c6]/60', 'transition-all', 'duration-300');
    
    // Add subtle pulse animation for labels
    const labelElement = document.querySelector(`label[for="${name}"]`) || 
                         inputEl.parentElement?.previousElementSibling as HTMLElement;
    
    if (labelElement) {
      labelElement.classList.add('text-[#6741c6]', 'transition-all', 'duration-300');
    }
    
    // Add subtle effect to parent container
    if (inputEl.parentElement) {
      inputEl.parentElement.classList.add('bg-slate-50/50', 'transition-all', 'duration-300');
    }
  };
  
  // Handle blur events
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const inputEl = e.target;
    
    // Remove focus effects
    inputEl.classList.remove('shadow-md', 'border-[#6741c6]/60');
    
    // Remove effects from label
    const labelElement = document.querySelector(`label[for="${name}"]`) || 
                         inputEl.parentElement?.previousElementSibling as HTMLElement;
    
    if (labelElement) {
      labelElement.classList.remove('text-[#6741c6]');
    }
    
    // Remove effects from parent
    if (inputEl.parentElement) {
      inputEl.parentElement.classList.remove('bg-slate-50/50');
    }
    
    // Only validate again if field was touched and has value
    if (touchedFields[name] && value) {
      const errorMessage = validateField(name, value);
      if (errorMessage) {
        setErrors(prev => ({ ...prev, [name]: errorMessage }));
        setValidFields(prev => ({ ...prev, [name]: false }));
        
        // Add stronger shake animation for invalid fields on blur
        inputEl.classList.add('animate-wiggle', 'border-red-500');
        
        // Animate the error message appearance
        const errorElement = inputEl.parentElement?.parentElement?.querySelector('.text-red-500');
        if (errorElement) {
          errorElement.classList.add('animate-fadeIn');
        }
        
        setTimeout(() => {
          inputEl.classList.remove('animate-wiggle');
        }, 500);
      } else {
        // Add success animation
        inputEl.classList.add('border-green-500');
        
        // Add slight pulse to the checkmark icon if present
        const checkIcon = inputEl.parentElement?.querySelector('svg.text-green-500');
        if (checkIcon) {
          checkIcon.classList.add('animate-popIn');
          setTimeout(() => {
            checkIcon.classList.remove('animate-popIn');
          }, 500);
        }
        
        setValidFields(prev => ({ ...prev, [name]: true }));
      }
    }
  };
  
  const handleFileChange = (name: keyof FormState) => (file: File | null) => {
    setFormState(prev => ({ ...prev, [name]: file }));
    
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Get the file upload container for animations
    let containerElement: HTMLElement | null = null;
    
    if (name === 'fundLogo' || name === 'mainGraphic') {
      const uploaderId = name === 'fundLogo' ? 'fund-logo' : 'main-graphic';
      containerElement = document.getElementById(uploaderId)?.closest('.image-upload-container') || null;
    } else if (name === 'agreementDoc') {
      containerElement = document.querySelector('.border-dashed') as HTMLElement;
    }
    
    // Validate file field
    if (name === 'fundLogo' || name === 'agreementDoc') {
      if (!file) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: name === 'fundLogo' ? 'Fund logo is required' : 'Agreement document is required' 
        }));
        setValidFields(prev => ({ ...prev, [name]: false }));
        
        // Show error animation
        if (containerElement) {
          containerElement?.classList.add('animate-shake', 'border-red-500');
          setTimeout(() => {
            containerElement?.classList.remove('animate-shake');
            // Keep border-red-500 to indicate error state
          }, 500);
        }
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
        setValidFields(prev => ({ ...prev, [name]: true }));
        
        // Show success animation
        if (containerElement) {
          containerElement.classList.remove('border-red-500');
          containerElement.classList.add('animate-popIn', 'border-green-500');
          
          // Add a subtle success checkmark animation
          const successIcon = document.createElement('div');
          successIcon.innerHTML = `
            <svg class="absolute top-2 right-2 w-6 h-6 text-green-500 animate-popIn" 
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" 
                    stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          `;
          
          // Append success icon if not already present
          if (!containerElement.querySelector('.text-green-500')) {
            containerElement.style.position = 'relative';
            containerElement.appendChild(successIcon.firstElementChild as Node);
            
            // Remove after 3 seconds
            setTimeout(() => {
              const icon = containerElement?.querySelector('.text-green-500');
              if (icon) {
                icon.classList.add('animate-fadeIn');
                icon.addEventListener('animationend', () => {
                  icon.remove();
                });
              }
            }, 3000);
          }
          
          setTimeout(() => {
            containerElement?.classList.remove('animate-popIn');
            // Keep the green border for a while
            setTimeout(() => {
              containerElement?.classList.remove('border-green-500');
            }, 2000);
          }, 500);
        }
      }
    } else if (file) {
      // For optional files, just mark as valid if present
      setValidFields(prev => ({ ...prev, [name]: true }));
      
      // Show subtle success animation
      if (containerElement) {
        containerElement?.classList.add('animate-pulse');
        setTimeout(() => {
          containerElement?.classList.remove('animate-pulse');
        }, 500);
      }
    }
    
    // Show toast notification for file upload
    if (file) {
      const fileSize = (file.size / (1024 * 1024)).toFixed(2);
      notifyInfo(`File "${file.name}" (${fileSize} MB) selected`, NotificationPriority.DEBUG, {
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'animate-slideInRight',
      });
    }
  };
  
  const handleDateClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      // Add animation to the date picker trigger element
      const datePickerElement = inputRef.current.parentElement;
      if (datePickerElement) {
        datePickerElement.classList.add('scale-[1.02]', 'shadow-md', 'border-[#6741c6]/60', 'transition-all', 'duration-200');
        
        // Reset after the date picker is shown
        setTimeout(() => {
          datePickerElement.classList.remove('scale-[1.02]');
        }, 300);
      }
      
      // Show the native date picker
      inputRef.current.showPicker();
      
      // Reset the visual style when the date picker closes (approximated by a timeout)
      setTimeout(() => {
        if (datePickerElement) {
          datePickerElement.classList.remove('shadow-md', 'border-[#6741c6]/60');
        }
      }, 3000); // Approximate time a user interacts with the date picker
    }
  };
  
  const handleDateChange = (name: 'launchDate' | 'closingDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const datePickerElement = event.target.parentElement;
    
    setFormState(prev => ({ ...prev, [name]: value }));
    
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Validate date field
    const errorMessage = validateField(name, value);
    if (errorMessage) {
      setErrors(prev => ({ ...prev, [name]: errorMessage }));
      setValidFields(prev => ({ ...prev, [name]: false }));
      
      // Error animation
      if (datePickerElement) {
        datePickerElement.classList.add('animate-shake', 'border-red-500');
        setTimeout(() => {
          datePickerElement.classList.remove('animate-shake');
        }, 500);
        
        // Animate the error message appearance
        const errorElement = datePickerElement.parentElement?.querySelector('.text-red-500');
        if (errorElement) {
          errorElement.classList.add('animate-fadeIn');
        }
      }
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      
      // Success animation
      if (datePickerElement) {
        datePickerElement.classList.remove('border-red-500');
        datePickerElement.classList.add('border-green-500', 'animate-popIn');
        
        // Add a subtle pulse to the calendar icon
        const calendarIcon = datePickerElement.querySelector('svg');
        if (calendarIcon) {
          calendarIcon.classList.add('animate-bounce');
          setTimeout(() => {
            calendarIcon.classList.remove('animate-bounce');
          }, 500);
        }
        
        // Add check icon if not present
        const checkIcon = datePickerElement.querySelector('.text-green-500');
        if (!checkIcon && datePickerElement.querySelector('svg')) {
          const checkIconDiv = document.createElement('div');
          checkIconDiv.innerHTML = `
            <svg class="w-5 h-5 text-green-500 mr-2 animate-popIn" 
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" 
                    stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          `;
          
          datePickerElement.insertBefore(
            checkIconDiv.firstElementChild as Node, 
            datePickerElement.lastElementChild
          );
        }
        
        setTimeout(() => {
          datePickerElement.classList.remove('animate-popIn');
        }, 500);
      }
      
      setValidFields(prev => ({ ...prev, [name]: true }));
      
      // Toast notification for date selection
      const formattedDate = new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      notifySuccess(`${name === 'launchDate' ? 'Launch' : 'Closing'} date set to ${formattedDate}`, NotificationPriority.MEDIUM, {
        autoClose: 2000,
        className: 'animate-fadeIn',
      });
      
      // If this is launch date, also re-validate closing date if it exists
      if (name === 'launchDate' && formState.closingDate) {
        const closingDateError = validateField('closingDate', formState.closingDate);
        
        // Get the closing date picker element
        const closingDateElement = closingDateInputRef.current?.parentElement;
        
        if (closingDateError) {
          setErrors(prev => ({ ...prev, closingDate: closingDateError }));
          setValidFields(prev => ({ ...prev, closingDate: false }));
          
          // Show error animation on closing date
          if (closingDateElement) {
            closingDateElement.classList.add('animate-shake', 'border-red-500');
            closingDateElement.classList.remove('border-green-500');
            
            // Remove any success check icons
            const checkIcon = closingDateElement.querySelector('.text-green-500');
            if (checkIcon) {
              checkIcon.remove();
            }
            
            setTimeout(() => {
              closingDateElement.classList.remove('animate-shake');
            }, 500);
          }
        } else if (errors.closingDate) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.closingDate;
            return newErrors;
          });
          setValidFields(prev => ({ ...prev, closingDate: true }));
          
          // Show validation recovery animation on closing date
          if (closingDateElement) {
            closingDateElement.classList.remove('border-red-500');
            closingDateElement.classList.add('border-green-500', 'animate-popIn');
            
            setTimeout(() => {
              closingDateElement.classList.remove('animate-popIn');
            }, 500);
          }
        }
      }
    }
  };
  
  const handleToggleChange = (name: keyof FormState) => () => {
    const newValue = typeof formState[name] === 'boolean' ? !formState[name] : true;
    
    setFormState(prev => ({ 
      ...prev, 
      [name]: newValue
    }));
    
    // Find the toggle button
    const toggleButton = document.getElementById(name as string);
    if (toggleButton) {
      // Add bounce animation to the toggle
      toggleButton.classList.add('scale-110');
      setTimeout(() => {
        toggleButton.classList.remove('scale-110');
      }, 300);
      
      // Add ripple effect
      const ripple = document.createElement('span');
      ripple.classList.add('absolute', 'w-full', 'h-full', 'rounded-full', 'bg-white', 'opacity-30', 'transform', 'scale-0');
      
      // Position the ripple
      toggleButton.style.overflow = 'hidden';
      toggleButton.appendChild(ripple);
      
      // Animate the ripple
      ripple.animate(
        [
          { transform: 'scale(0)', opacity: '0.3' },
          { transform: 'scale(4)', opacity: '0' }
        ],
        {
          duration: 600,
          easing: 'ease-out'
        }
      ).onfinish = () => ripple.remove();
      
      // Add a toast notification
      if (name === 'accreditedOnly') {
        notifyInfo(`Accredited investors only: ${newValue ? 'Enabled' : 'Disabled'}`, NotificationPriority.MEDIUM, {
          autoClose: 2000,
          className: 'animate-fadeIn',
          // Removing icon to fix TypeScript error
          // icon: () => (newValue ? '🔒' : '🔓')
        });
      }
    }
  };
  
  const handleOfferingTypeChange = (type: OfferingType) => {
    setFormState(prev => ({ ...prev, offeringType: type }));
    
    // Find the selected option element
    const optionElements = document.querySelectorAll('[data-offering-type]');
    
    // Animation for the selected option
    optionElements.forEach(element => {
      if (element.getAttribute('data-offering-type') === type) {
        element.classList.add('scale-[1.02]', 'shadow-md', 'animate-popIn');
        setTimeout(() => {
          element.classList.remove('scale-[1.02]', 'animate-popIn');
          // Keep the shadow for emphasis
        }, 500);
      } else {
        // Subtle fade for unselected options
        element.classList.add('opacity-70', 'transition-opacity', 'duration-300');
        setTimeout(() => {
          element.classList.remove('opacity-70');
        }, 300);
      }
    });
    
    // Show toast with offering type information
    const offeringTypeInfo = {
      'public': {
        title: 'Public Offering Selected',
        description: 'Open to all investors, max 250 participants',
        iconEmoji: '🌐'
      },
      'private': {
        title: 'Private Placement Selected',
        description: 'Limited to select investors, max 100 participants',
        iconEmoji: '🔒'
      },
      'auction': {
        title: 'Auction Offering Selected',
        description: 'Competitive bidding process, max 500 participants',
        iconEmoji: '🔨'
      }
    };
    
    const info = offeringTypeInfo[type];
    
    notifySuccess(
      <div className="flex items-center">
        <span className="mr-2">{info.iconEmoji}</span>
        <span>{info.title}</span>
      </div>,
      NotificationPriority.MEDIUM,
      {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'animate-slideInRight'
      }
    );
  };
  
  const handleOverviewChange = (data: any) => {
    const parsedParagraphs = editorStateToFsParagraphs(data);
    
    setFormState(prev => ({
      ...prev,
      overview: {
        ...prev.overview,
        paragraphs: parsedParagraphs,
      },
    }));
  };
  
  // File upload handler
  const uploadFileToCloudinary = async (file: File) => {
    const uploadPreset =
      process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET ?? 'default_upload_preset';
    const cloudName =
      process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ?? 'default_cloud_name';

    // Show uploading toast (medium priority)
    const toastId = notifyInfo(`Uploading ${file.name}...`, NotificationPriority.MEDIUM, {
      autoClose: false,
    });

    try {
      const data = new FormData();
      data.append('file', file);
      data.append('upload_preset', uploadPreset);
      data.append('cloud_name', cloudName);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: data,
        }
      );
      
      const result = await response.json();
      
      // Check if upload was successful
      if (!response.ok || !result.secure_url) {
        throw new Error(result.error?.message || 'Failed to upload file');
      }
      
      // Close uploading toast and show success (low priority)
      if (toastId) {
        toast.dismiss(toastId);
      }
      notifySuccess(`${file.name} uploaded successfully`, NotificationPriority.LOW, {
        autoClose: 3000,
      });
      
      return result.secure_url;
    } catch (error) {
      // Close uploading toast and show error (high priority)
      if (toastId) {
        toast.dismiss(toastId);
      }
      notifyError(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`, NotificationPriority.HIGH, {
        autoClose: 5000,
      });
      throw error;
    }
  };
  
  // Handle AI import from URL
  const handleFillDetailsWithAI = async () => {
    if (!formState.importUrl) {
      notifyError('Please enter a valid URL', NotificationPriority.MEDIUM, {
        autoClose: 3000,
      });
      return;
    }

    setIsFetchingAI(true);

    try {
      const payload = {
        url: formState.importUrl,
        formats: ['extract'],
        extract: {
          systemPrompt:
            'You are a fund data extraction expert. Provide only the JSON object as the final output.',
          prompt: `Extract ONLY the following from the fund page and provide the output as a JSON object with no additional text or formatting:

1. "FundName": The main fund name.
2. "Tagline": A subtitle or a catchy phrase summarizing the fund.
3. "Overview": A brief description (2-3 sentences).
4. "TokenPrice": The token price if available (numeric value only).
5. "MinInvestment": The minimum investment amount if available (numeric value only).
6. "TargetRaise": The target raise amount if available (numeric value only).

If any field is not found, attempt to infer it from the content. If impossible, omit it from the JSON.`,
        },
      };

      const response = await fetch(
        'https://o4kc8w08sskcgc48o4gkwgos.sugem.io/v1/scrape',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        const extractedData = data.data.extract;

        // Update form state with extracted data
        setFormState(prev => ({
          ...prev,
          fundName: extractedData.FundName || prev.fundName,
          tagline: extractedData.Tagline || prev.tagline,
          tokenPrice: extractedData.TokenPrice || prev.tokenPrice,
          minInvestment: extractedData.MinInvestment || prev.minInvestment,
          targetRaise: extractedData.TargetRaise || prev.targetRaise,
          overview: {
            paragraphs: [
              {
                text: extractedData.Overview || '',
              },
            ],
          },
        }));

        notifySuccess('Details filled successfully!', NotificationPriority.LOW, {
          autoClose: 3000,
        });
      } else {
        notifyError('Failed to fetch data from AI', NotificationPriority.HIGH, {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error fetching AI data:', error);
      notifyError('An error occurred while fetching data', NotificationPriority.HIGH, {
        autoClose: 3000,
      });
    } finally {
      setIsFetchingAI(false);
    }
  };
  
  // Mock function for file import - takes a File directly as parameter
  const handleFileImport = (file: File) => {
    setIsImportingFile(true);
    
    // Simulate parsing the file
    setTimeout(() => {
      // Mock data extraction
      setFormState(prev => ({
        ...prev,
        fundName: "Acme Ventures Fund III",
        tagline: "Early-stage technology and innovation investments",
        tokenPrice: "5.00",
        minInvestment: "5000",
        targetRaise: "25000000",
        overview: {
          paragraphs: [
            {
              text: "Acme Ventures Fund III focuses on early-stage investments in AI, blockchain, and sustainable technologies. The fund targets companies with strong founding teams and disruptive business models that can scale globally.",
            },
          ],
        },
        offeringType: "private",
        accreditedOnly: true,
      }));
      
      notifySuccess(`Successfully imported details from "${file.name}"`, NotificationPriority.MEDIUM, {
        autoClose: 3000,
      });
      
      setIsImportingFile(false);
    }, 2000);
  };
  
  // Template creation handler
  const handleCreateTemplate = useCallback(
    async (projectId: string) => {
      if (!formState.agreementDoc) return { success: false, templateId: null };
      try {
        notifyInfo('Creating legal agreement template...', NotificationPriority.MEDIUM, {
          autoClose: 3000,
        });
        const result = await createTemplate(
          `${projectId} template`,
          formState.agreementDoc
        );
        if (result.templateId) {
          notifySuccess('Legal agreement template created successfully', NotificationPriority.MEDIUM, {
            autoClose: 3000,
          });
          
          // Set template edited state to true in our state management
          setStateValue(STATE_KEYS.EDIT_STEP, projectId, true);
          
          return { 
            success: true, 
            templateId: result.templateId 
          };
        } else {
          notifyError('Failed to create legal agreement template', NotificationPriority.HIGH, {
            autoClose: 3000,
          });
          return { 
            success: false, 
            templateId: null 
          };
        }
      } catch (error) {
        console.error('Error creating template:', error);
        notifyError('Error creating legal agreement template. Please try again.', NotificationPriority.HIGH, {
          autoClose: 3000,
        });
        return { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error',
          templateId: null
        };
      }
    },
    [formState.agreementDoc, createTemplate]
  );
  
  // Cancel handler
  const handleCancel = () => {
    navigate('/sales');
  };
  
  // Redirect to template editor
  const redirectToEditTemplate = useCallback(async (projectId: string, templateId: string) => {
    try {
      notifyInfo('Preparing to redirect to template editor...', NotificationPriority.LOW, {
        autoClose: 3000,
      });
      
      // Create a return URL with parameters to track the return from template editing
      const currentUrl = new URL(window.location.origin);
      const redirectUrl = `${currentUrl.protocol}//${currentUrl.host}/sales?returnFromTemplate=true&projectId=${projectId}`;
      
      const result = await getTemplateEditViewUrl(templateId, redirectUrl);
      
      if (result?.url) {
        // Update state for this specific project
        setStateValue(STATE_KEYS.EDIT_TEMPLATE, projectId, true);
        
        // Show a toast about redirection
        notifySuccess('Redirecting to DocuSign template editor...', NotificationPriority.MEDIUM, {
          autoClose: 3000
        });
        
        // Redirect to DocuSign
        window.location.href = result.url;
        return true;
      } else {
        // If redirect URL couldn't be generated, navigate to sales page instead
        notifyWarning('Could not redirect to template editor automatically. Please edit the template manually.', NotificationPriority.MEDIUM, {
          autoClose: 5000
        });
        navigate('/sales');
        return false;
      }
    } catch (error) {
      console.error('Error redirecting to template editor:', error);
      notifyError('Failed to redirect to template editor.', NotificationPriority.MEDIUM, {
        autoClose: 3000
      });
      return false;
    }
  }, [navigate]);
  
  // Update form status based on validation
  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasAtLeastOneTouchedField = Object.keys(touchedFields).length > 0;
    
    if (isUploading) {
      setFormStatus('saving');
    } else if (hasErrors && hasAtLeastOneTouchedField) {
      setFormStatus('error');
    } else if (hasAtLeastOneTouchedField && !hasErrors) {
      setFormStatus('valid');
    } else {
      setFormStatus('idle');
    }
  }, [errors, touchedFields, isUploading]);

  // Form submission handler
  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsUploading(true);
    
    // Scroll to top to show the loading animation
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      // Validate form data first
      const validationResult = schema.safeParse({
        fundName: formState.fundName,
        tagline: formState.tagline,
        fundLogo: formState.fundLogo,
        mainGraphic: formState.mainGraphic,
        overview: formState.overview,
        tokenPrice: formState.tokenPrice,
        minInvestment: formState.minInvestment,
        targetRaise: formState.targetRaise,
        offeringType: formState.offeringType || 'public',
        launchDate: formState.launchDate || '',
        closingDate: formState.closingDate || '',
        agreementDoc: formState.agreementDoc,
      });
      
      if (!validationResult.success) {
        const validationErrors: Record<string, string> = {};
        validationResult.error.errors.forEach((err) => {
          if (err.path[0]) {
            validationErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(validationErrors);
        
        // Enhanced error toast with animation and detail
        toast.error(
          <div className="animate-fadeIn">
            <div className="font-medium">Form Validation Failed</div>
            <div className="text-sm mt-1">Please fix the highlighted errors before submitting.</div>
            <ul className="text-xs mt-2 list-disc pl-4 max-h-24 overflow-y-auto">
              {validationResult.error.errors.slice(0, 3).map((err, idx) => (
                <li key={idx} className="animate-slideInLeft" style={{ animationDelay: `${idx * 100}ms` }}>
                  {err.path[0] ? `${err.path[0].toString()}: ` : ''}{err.message}
                </li>
              ))}
              {validationResult.error.errors.length > 3 && (
                <li className="animate-slideInLeft" style={{ animationDelay: '300ms' }}>
                  ...and {validationResult.error.errors.length - 3} more errors
                </li>
              )}
            </ul>
          </div>,
          {
            autoClose: 7000,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            className: 'bg-red-50 border-l-4 border-red-500',
            // Removing icon to fix TypeScript error
            // icon: () => '⚠️',
          }
        );
        
        // Highlight the sections with errors
        const sections = [0, 1, 2, 3, 4]; // Sections 0-4
        const sectionErrors: Record<number, boolean> = {};
        
        // Map errors to sections
        Object.keys(validationErrors).forEach(errorKey => {
          if (['fundName', 'fundLogo', 'mainGraphic', 'overview'].includes(errorKey)) {
            sectionErrors[0] = true;
          } else if (['tokenPrice', 'minInvestment', 'targetRaise'].includes(errorKey)) {
            sectionErrors[1] = true;
          } else if (['offeringType'].includes(errorKey)) {
            sectionErrors[2] = true;
          } else if (['launchDate', 'closingDate'].includes(errorKey)) {
            sectionErrors[3] = true;
          } else if (['agreementDoc'].includes(errorKey)) {
            sectionErrors[4] = true;
          }
        });
        
        // Scroll to the first section with errors
        const firstErrorSection = sections.find(section => sectionErrors[section]);
        if (firstErrorSection !== undefined) {
          scrollToSection(firstErrorSection);
          
          // Add shake animation to the section with errors
          const sectionElement = sectionRefs.current[firstErrorSection]?.current;
          if (sectionElement) {
            sectionElement.classList.add('animate-shake', 'border-red-300');
            setTimeout(() => {
              sectionElement.classList.remove('animate-shake');
              // Keep the border for a while to highlight the section
              setTimeout(() => {
                sectionElement.classList.remove('border-red-300');
              }, 3000);
            }, 500);
          }
        }
        
        setIsUploading(false);
        return;
      }
      
      // Clear previous validation errors
      setErrors({});
      
      // Step 1: Upload images if needed
      let uploadedFundLogoUrl = uploadedUrls.fundLogoUrl;
      let uploadedMainGraphicUrl = uploadedUrls.mainGraphicUrl;

      try {
        // Create a dynamic progress toast for file uploads
        const uploadToastId = notifyInfo(
          <div className="flex flex-col">
            <div className="font-medium">Uploading files</div>
            <div className="mt-2 space-y-2">
              {formState.fundLogo && (
                <div className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="text-sm truncate max-w-xs">{formState.fundLogo.name}</div>
                </div>
              )}
              {formState.mainGraphic && (
                <div className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="text-sm truncate max-w-xs">{formState.mainGraphic.name}</div>
                </div>
              )}
              {formState.agreementDoc && (
                <div className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="text-sm truncate max-w-xs">{formState.agreementDoc.name}</div>
                </div>
              )}
            </div>
          </div>,
          NotificationPriority.MEDIUM,
          {
            autoClose: false,
            className: 'animate-fadeIn',
          }
        );
        
        // Upload files in parallel for better performance
        const uploadPromises = [];
        
        if (formState.fundLogo) {
          uploadPromises.push(
            uploadFileToCloudinary(formState.fundLogo)
              .then(url => { uploadedFundLogoUrl = url; })
          );
        }
        
        if (formState.mainGraphic) {
          uploadPromises.push(
            uploadFileToCloudinary(formState.mainGraphic)
              .then(url => { uploadedMainGraphicUrl = url; })
          );
        }
        
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        // Update the state with uploaded URLs
        setUploadedUrls({
          fundLogoUrl: uploadedFundLogoUrl,
          mainGraphicUrl: uploadedMainGraphicUrl,
        });
        
        // Close the upload toast
        if (uploadToastId) {
          toast.update(uploadToastId, {
          render: (
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <div>Files uploaded successfully!</div>
            </div>
          ),
          type: 'success',
          autoClose: 3000,
          className: 'animate-popIn',
        });
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        notifyError(
          <div>
            <div className="font-medium">Upload Failed</div>
            <div className="text-sm">{error instanceof Error ? error.message : 'Unknown error'}</div>
          </div>,
          NotificationPriority.HIGH,
          {
            autoClose: 5000,
            className: 'animate-shake',
          }
        );
        setIsUploading(false);
        return;
      }

      // Step 2: Create the project
      let createdProjectId: string;
      try {
        const createProjectToastId = notifyInfo(
          <div className="flex items-center">
            <div className="animate-pulse mr-2">🔄</div>
            <div>
              <div className="font-medium">Creating Fund</div>
              <div className="text-xs opacity-80">Setting up fund details...</div>
            </div>
          </div>,
          NotificationPriority.MEDIUM,
          {
            autoClose: false,
            className: 'animate-slideInRight',
          }
        );
        
        const apiResult = await createProject({
          name: formState.fundName,
          description: formState.tagline || '',
          tags: [],
          chain: '80002', // Chain ID for testnet
          logo_image_url: uploadedFundLogoUrl || '',
          main_image_url: uploadedMainGraphicUrl || '',
          start_date_whitelist: null,
          end_date_whitelist: null,
          start_date_funding: formState.launchDate,
          end_date_funding: formState.closingDate,
          ido_token_symbol: 'SHARE',
          pay_token_symbol: 'USDC',
          cap_total_dollars: formState.targetRaise,
          cap_individual_dollars: formState.minInvestment,
          ratio_idotoken_per_paytoken: 1 / Number(formState.tokenPrice),
          ratio_paytoken_per_idotoken: formState.tokenPrice,
          sale_version: 3,
          with_whitelist: formState.offeringType === 'private',
          with_lottery: formState.offeringType === 'auction',
          only_staked: false,
          with_kyc: formState.accreditedOnly,
          header_image_url: uploadedMainGraphicUrl,
          company: companyName,
          location: null,
        });
        
        if (createProjectToastId) {
          toast.dismiss(createProjectToastId);
        }
        
        if (apiResult.status !== 200 || !apiResult.data?.result?.data?.projectId) {
          throw new Error('Failed to create fund: ' + JSON.stringify(apiResult.data?.error || {}));
        }
        
        createdProjectId = apiResult.data.result.data.projectId;
        
        notifySuccess(
          <div className="flex items-center animate-fadeIn">
            <div className="mr-2">🚀</div>
            <div>
              <div className="font-medium">Fund Created Successfully!</div>
              <div className="text-xs">Project ID: {createdProjectId}</div>
            </div>
          </div>,
          NotificationPriority.HIGH,
          {
            autoClose: 3000,
            closeOnClick: true,
            className: 'bg-green-50 border-l-4 border-green-500',
          }
        );
      } catch (error) {
        console.error('Error creating fund:', error);
        notifyError(
          <div className="animate-fadeIn">
            <div className="font-medium">Fund Creation Failed</div>
            <div className="text-sm mt-1">{error instanceof Error ? error.message : 'Unknown error'}</div>
            <div className="text-xs mt-2">Please try again or contact support if the problem persists.</div>
          </div>,
          NotificationPriority.HIGH,
          {
            autoClose: 7000,
            closeOnClick: true,
            pauseOnHover: true,
            className: 'bg-red-50 border-l-4 border-red-500',
            // Removing icon to fix TypeScript error
            // icon: () => '❌',
          }
        );
        setIsUploading(false);
        return;
      }

      // Step 3: Update project details, summary, and create template in parallel
      try {
        const updateToastId = notifyInfo(
          <div>
            <div className="font-medium">Finalizing Fund Setup</div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
                Updating fund details...
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
                Saving fund overview...
              </div>
              {formState.agreementDoc && (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  </div>
                  Creating legal agreement template...
                </div>
              )}
            </div>
          </div>,
          NotificationPriority.MEDIUM,
          {
            autoClose: false,
            className: 'animate-fadeIn',
          }
        );
        
        // Run these operations in parallel
        const results = await Promise.allSettled([
          // Update project details
          updateProjectDetails({
            id: createdProjectId,
            project_id: createdProjectId,
            name: formState.fundName,
            tags: [],
            logo_image_url: uploadedFundLogoUrl || '',
            chain: 80002,
            pay_token_symbol: 'USDC',
            cap_total_dollars: formState.targetRaise,
            cap_individual_dollars: formState.minInvestment,
            ratio_paytoken_per_idotoken: formState.tokenPrice,
          })
            .then(response => {
              if (response.status === 200) {
                localStorage.setItem(`editStepDone-${createdProjectId}`, 'true');
                return { success: true };
              } else {
                console.warn('Update fund details warning:', response);
                return { success: false, error: 'Failed to update details' };
              }
            })
            .catch(error => {
              console.error('Error updating fund details:', error);
              return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
            }),
          
          // Save project summary
          saveProjectSummary(createdProjectId, formState.overview)
            .then(() => ({ success: true }))
            .catch(error => {
              console.error('Error saving fund overview:', error);
              return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
            }),
          
          // Create template for the legal agreement
          formState.agreementDoc 
            ? handleCreateTemplate(createdProjectId)
                .then(result => result) // Return the actual result with templateId
                .catch(error => ({ success: false, error, templateId: null }))
            : Promise.resolve({ success: true, skipped: true, templateId: null })
        ]);
        
        if (updateToastId) {
          toast.dismiss(updateToastId);
        }
        
        // Get the template ID from the create template result
        const templateResult = results.find(r => 
          r.status === 'fulfilled' && 
          r.value?.success && 
          'templateId' in r.value && 
          r.value.templateId !== null
        );
        
        // Check for any failed operations
        const failedOperations = results.filter(
          r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
        );
        
        if (failedOperations.length > 0) {
          // Show warning with details about partial success
          toast.warning(
            <div>
              <div className="font-medium">Fund Created with Warnings</div>
              <div className="text-sm mt-1">Some operations completed with issues:</div>
              <ul className="text-xs mt-1 list-disc pl-4">
                {failedOperations.map((op, idx) => {
                  const error = op.status === 'rejected' 
                    ? op.reason 
                    : (op.value as any).error;
                  return (
                    <li key={idx} className="mt-0.5">
                      {typeof error === 'string' ? error : 'Operation failed'}
                    </li>
                  );
                })}
              </ul>
              <div className="text-xs mt-2 italic">You may need to update some details manually.</div>
            </div>,
            {
              autoClose: 7000,
              // Removing icon to fix TypeScript error
            // icon: () => '⚠️',
            }
          );
          
          // Even with warnings, if we got a template ID, we can use it
          if (templateResult && 
              templateResult.status === 'fulfilled' && 
              'templateId' in templateResult.value && 
              templateResult.value.templateId) {
            setTimeout(() => {
              // Cast to any to avoid TypeScript errors with property access
              const templateId = (templateResult.value as any).templateId;
              redirectToEditTemplate(
                createdProjectId, 
                templateId
              );
            }, 1500);
          } else {
            setTimeout(() => {
              navigate('/sales');
            }, 1500);
          }
        } else {
          // All operations succeeded
          toast.success(
            <div className="animate-popIn">
              <div className="font-medium">Fund Setup Complete!</div>
              <div className="text-sm mt-1">Your fund has been successfully created and configured.</div>
              <div className="text-xs mt-2 text-green-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
                {templateResult && templateResult.status === 'fulfilled' && 
                  'templateId' in templateResult.value && templateResult.value.templateId ? 
                  'Redirecting to template editor...' : 
                  'Redirecting to Sales dashboard...'}
              </div>
            </div>,
            {
              autoClose: 3000,
              closeOnClick: true,
              className: 'bg-green-50 border-l-4 border-green-500',
              // Removing icon to fix TypeScript error
              // icon: () => '✅',
            }
          );
          
          // Check if we have a valid template ID to redirect to
          if (templateResult && 
              templateResult.status === 'fulfilled' && 
              'templateId' in templateResult.value && 
              templateResult.value.templateId) {
            // Brief delay before redirecting to template editor
            setTimeout(() => {
              // Cast to any to avoid TypeScript errors with property access
              const templateId = (templateResult.value as any).templateId;
              redirectToEditTemplate(
                createdProjectId, 
                templateId
              );
            }, 1500);
          } else {
            // If no template ID, just navigate to sales page
            setTimeout(() => {
              navigate('/sales');
            }, 1500);
          }
        }
      } catch (error) {
        console.error('Error in fund setup:', error);
        notifyWarning(
          <div>
            <div className="font-medium">Setup Incomplete</div>
            <div className="text-sm mt-1">Fund was created but some details could not be updated.</div>
            <div className="text-xs mt-2 text-blue-600">
              Redirecting to Sales dashboard where you can edit the fund...
            </div>
          </div>,
          NotificationPriority.HIGH,
          {
            autoClose: 5000,
            className: 'bg-orange-50 border-l-4 border-orange-500',
          }
        );
        
        // Brief delay before navigation for toast to be seen
        setTimeout(() => {
          navigate('/sales');
        }, 2000);
      }
    } catch (error) {
      console.error('Unexpected error during form submission:', error);
      notifyError(
        <div>
          <div className="font-medium">Unexpected Error</div>
          <div className="text-sm mt-1">{error instanceof Error ? error.message : 'An unknown error occurred'}</div>
          <div className="text-xs mt-2">Please try again or contact support.</div>
        </div>,
        NotificationPriority.HIGH,
        {
          autoClose: 7000,
          closeOnClick: false,
          className: 'bg-red-50 border-l-4 border-red-500',
        }
      );
    } finally {
      setIsUploading(false);
    }
  };
  
  // FileDropImport component handles file selection now
  
  return (
    <DesktopLayout>
      <main className="pt-16 px-0 flex flex-col items-center overflow-x-hidden w-full">
        {/* Breadcrumb ribbon navigation - slim and lightweight */}
        {/* 
        <div className="sticky top-20 z-50 w-full mb-4">
          <div className="bg-slate-100 py-2 px-4 rounded-md shadow-sm">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center text-sm font-medium text-slate-700 hover:text-[#6741c6] transition-colors duration-200"
              >
                <img 
                  src={arrowLeft} 
                  alt="Back" 
                  className="mr-1.5 h-3.5 w-3.5" 
                />
                Back
              </button>
              
              <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide">
                {['Fund Details', 'Terms', 'Structure', 'Timeline', 'Agreement'].map((step, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <span className="text-slate-400 mx-0.5">•</span>
                    )}
                    <button
                      type="button"
                      onClick={() => scrollToSection(index)}
                      className={`px-2 py-1 text-xs rounded transition-colors duration-200 whitespace-nowrap ${
                        activeSection === index 
                          ? 'text-[#6741c6] font-medium' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {index + 1} {step}
                    </button>
                    {index < 4 && (
                      <span className="text-slate-400 hidden md:inline">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              <div className="hidden sm:flex items-center">
                <div className="flex items-center text-xs">
                  {formStatus === 'error' && (
                    <div className="flex items-center text-red-500">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                      </svg>
                      {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        */}

        <form id="create-fund-form" className="w-[calc(100%-32px)] max-w-[800px] mx-auto mt-16 overflow-hidden" onSubmit={handleSave}>
          {/* Form status and save button */}
          {/*
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              {formStatus === 'valid' && (
                <div className="flex items-center text-green-500 text-sm">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Form is valid
                </div>
              )}
              {formStatus === 'idle' && (
                <div className="flex items-center text-slate-500 text-sm">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Complete the form to save
                </div>
              )}
            </div>
            
            <button
              type="submit"
              className={`relative btn-primary w-36 h-10 text-base font-medium py-2 transition-all duration-300
                hover:shadow-lg hover:bg-[#5536a5] transform hover:scale-[1.03] active:scale-[0.97]
                focus:ring-2 focus:ring-[#6741c6]/40 focus:outline-none overflow-hidden
                ${isUploading ? 'cursor-not-allowed' : ''}`}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <span 
                    className="absolute inset-0 bg-gradient-to-r from-[#6741c6]/0 via-white/20 to-[#6741c6]/0 animate-shimmer" 
                    style={{ backgroundSize: '200% 100%' }}
                  ></span>
                  
                  <span className="absolute bottom-0 left-0 h-1 bg-white animate-expandWidth"></span>
                  
                  <span className="flex items-center justify-center relative z-10">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="whitespace-nowrap">Saving Fund...</span>
                  </span>
                </>
              ) : (
                <span className="flex items-center justify-center">
                  <svg className="mr-1.5 h-4 w-4 group-hover:animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="relative">
                    Save Fund
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white transform scale-x-0 origin-left transition-transform group-hover:scale-x-100 duration-300"></span>
                  </span>
                </span>
              )}
            </button>
          </div>
          */}
          
          {/* Mobile Progress Tracker - only visible on mobile */}
          {/* Commented out as requested
          <div className="md:hidden mb-6">
            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
              <div 
                className="bg-[#6741c6] h-full rounded-full transition-all duration-300"
                style={{ width: `${(activeSection + 1) * 20}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Step {activeSection + 1} of 5</span>
              <span>{['Fund Details', 'Terms', 'Structure', 'Timeline', 'Agreement'][activeSection]}</span>
            </div>
          </div>
          */}

          {/* Page title */}
          <header className="flex flex-col mb-10 text-black w-full px-4">
            <h1 className="text-4xl font-semibold tracking-tight leading-tight max-md:max-w-full">
              Create a Tokenized Fund
            </h1>
            <p className="mt-2 text-lg font-medium tracking-normal text-gray-600 max-md:max-w-full">
              Ensure all fields are completed to launch your fund.
            </p>
          </header>
          
          {/* Import section - Enhanced with gradient background and tabbed interface */}
          <section className="mt-10 overflow-hidden rounded-lg shadow-lg border border-slate-200 mx-4 w-[calc(100%-32px)] max-w-[800px]">
            <div className="bg-gradient-to-r from-[#6741c6] to-[#9162e4] p-1 relative">
              <div className="flex items-center p-4 text-white">
                <img
                  src={sparkles}
                  alt=""
                  className="object-contain w-[36px] aspect-square mr-3 filter drop-shadow-md transition-all duration-500 hover:scale-110 hover:rotate-12 hover:drop-shadow-lg"
                />
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">
                    Fast-track Fund Setup
                  </h2>
                  <p className="text-sm opacity-90 mt-0.5 group">
                    Import data automatically to reduce setup time by
                    <span className="relative inline-block ml-1">
                      <span className="font-bold transition-all duration-300">80%</span>
                      <span className="absolute -inset-0.5 bg-white/20 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-sm"></span>
                    </span>
                  </p>
                </div>
              </div>
              
              {/* Tabbed interface */}
              <div className="flex flex-wrap px-4 pt-2 gap-1">
                <button
                  type="button"
                  className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-300 ${
                    !isImportingFile 
                      ? 'bg-white text-slate-900 shadow-sm translate-y-0'
                      : 'bg-white/20 text-white hover:bg-white/30 hover:shadow-inner'
                  }`}
                  onClick={() => setIsImportingFile(false)}
                >
                  <span className="flex items-center">
                    <svg className={`mr-1 sm:mr-1.5 h-4 w-4 transition-opacity duration-300 ${!isImportingFile ? 'opacity-100' : 'opacity-70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                    </svg>
                    <span className="whitespace-nowrap">Import via URL</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-300 ${
                    isImportingFile
                      ? 'bg-white text-slate-900 shadow-sm translate-y-0'
                      : 'bg-white/20 text-white hover:bg-white/30 hover:shadow-inner'
                  }`}
                  onClick={() => setIsImportingFile(true)}
                >
                  <span className="flex items-center">
                    <svg className={`mr-1 sm:mr-1.5 h-4 w-4 transition-opacity duration-300 ${isImportingFile ? 'opacity-100' : 'opacity-70'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <span className="whitespace-nowrap">Upload Prospectus</span>
                  </span>
                </button>
              </div>
            </div>
            
            <div className="bg-white p-6">
              {isImportingFile ? (
                <div className="py-2">
                  <p className="text-sm text-slate-600 mb-4">
                    Upload your fund's offering document. We'll extract key information automatically.
                  </p>
                  <FileDropImport 
                    onImport={handleFileImport}
                    isImporting={isImportingFile && isFetchingAI}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.json"
                    maxSizeMB={10}
                    className="max-w-2xl mx-auto"
                    showIcon={true}
                    compact={false}
                  />
                </div>
              ) : (
                <div className="py-2 max-w-2xl mx-auto">
                  <p className="text-sm text-slate-600 mb-4">
                    Enter the URL of your fund's webpage or offering document. Our AI will extract key details automatically.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label htmlFor="importUrl" className="block text-sm font-medium text-slate-700 mb-2">
                        Fund URL
                      </label>
                      <input
                        id="importUrl"
                        type="url"
                        name="importUrl"
                        value={formState.importUrl}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder="https://your-fund-website.com/offering"
                        className="w-full py-2.5 px-4 bg-white rounded-md border border-slate-300 shadow-sm 
                          focus:ring-2 focus:ring-[#6741c6]/30 focus:border-[#6741c6] focus:shadow-md focus:shadow-[#6741c6]/10
                          hover:border-[#6741c6]/50 
                          transition-all duration-200 text-[#111827] placeholder:text-slate-400 placeholder:opacity-60"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleFillDetailsWithAI}
                        disabled={isFetchingAI || !formState.importUrl}
                        data-action="extract"
                        className={`w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-300 ${
                          isFetchingAI || !formState.importUrl 
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                            : 'bg-[#6741c6] text-white hover:bg-[#5536a5] hover:shadow-lg transform hover:scale-[1.03] active:scale-[0.97] hover:shadow-[#6741c6]/20 relative overflow-hidden group'
                        }`}
                      >
                        {isFetchingAI ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                          </span>
                        ) : (
                          <>
                            <span className="flex items-center justify-center relative">
                              <svg className="mr-1.5 h-4 w-4 transition-transform duration-300 group-hover:scale-125 group-hover:rotate-3" 
                                fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                              </svg>
                              <span className="group-hover:font-medium">Extract with AI</span>
                              
                              {/* Subtle background effect on hover */}
                              <span className="absolute -inset-10 -z-10 bg-gradient-to-r from-indigo-100/20 to-purple-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full blur-md" 
                                aria-hidden="true"></span>
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-xs text-slate-500 flex items-center group hover:bg-slate-50 hover:text-slate-700 transition-all duration-300 p-2 rounded-md">
                    <svg className="w-4 h-4 mr-1.5 text-slate-400 group-hover:text-[#6741c6] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="group-hover:font-medium transition-all duration-200">
                      We extract fund name, description, investment terms, and timeline information automatically.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Main form sections - reorganized for logical flow */}
          <div className="mt-10 space-y-8 w-full">
            {/* Step 1: Fund Details section */}
            <section 
              ref={sectionRefs.current[0] as React.RefObject<HTMLElement>}
              className="bg-slate-50 border border-slate-300 rounded-md p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mx-4 w-[calc(100%-32px)] sm:mx-0 sm:w-full">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#6741c6] text-white font-semibold mr-3 shadow-sm">
                  1
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Fund Details</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4 ml-11">Enter basic information about your fund</p>
              <hr className="border-t border-slate-200 mb-6" />
                
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  <div className="w-full md:w-1/3">
                    <ImageUpload
                      id="fund-logo"
                      label="Fund Logo"
                      value={formState.fundLogo}
                      previewUrl={uploadedUrls.fundLogoUrl}
                      onChange={handleFileChange('fundLogo')}
                      error={errors.fundLogo}
                      accept="image/png, image/jpeg"
                      maxWidth={512}
                      maxHeight={512}
                      helpText="Upload PNG or JPG"
                      compact={true}
                    />
                  </div>
                  
                  <div className="w-full md:w-2/3">
                    <div className="mb-4">
                      <label htmlFor="fundName" className="block text-sm font-medium text-slate-900 mb-2">
                        Fund Name
                      </label>
                      <div className="relative">
                        <input
                          id="fundName"
                          name="fundName"
                          type="text"
                          value={formState.fundName}
                          onChange={handleInputChange}
                          placeholder="Enter fund name"
                          className={`w-full px-3 py-2 border ${
                            errors.fundName 
                              ? 'border-red-500 pr-10' 
                              : touchedFields.fundName && validFields.fundName
                                ? 'border-green-500 pr-10'
                                : 'border-slate-300'
                          } rounded-md bg-white text-[#111827] placeholder:text-slate-400 placeholder:opacity-60`}
                        />
                        {touchedFields.fundName && validFields.fundName && !errors.fundName && (
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                      {errors.fundName && (
                        <p className="mt-1 text-sm text-red-500">{errors.fundName}</p>
                      )}
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="tagline" className="block text-sm font-medium text-slate-900 mb-2">
                        Fund Tagline
                      </label>
                      <input
                        id="tagline"
                        name="tagline"
                        type="text"
                        value={formState.tagline}
                        onChange={handleInputChange}
                        placeholder="Enter fund tagline"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-[#111827] placeholder:text-slate-400 placeholder:opacity-60"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <ImageUpload
                    id="main-graphic"
                    label="Main Fund Graphic"
                    value={formState.mainGraphic}
                    previewUrl={uploadedUrls.mainGraphicUrl}
                    onChange={handleFileChange('mainGraphic')}
                    error={errors.mainGraphic}
                    accept="image/png, image/jpeg"
                    maxWidth={512}
                    maxHeight={512}
                    helpText="Drag or click to upload your hero image (PNG or JPG, max 512×512px)"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Fund Overview
                  </label>
                  <TextEditor
                    initialData={formState.overview.paragraphs}
                    withDescription={false}
                    getDatas={handleOverviewChange}
                    placeholder="Enter fund overview"
                  />
                  {errors.overview && (
                    <p className="mt-1 text-sm text-red-500">{errors.overview}</p>
                  )}
                </div>
                
                {/* Section navigation buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-end gap-3 sm:gap-5 overflow-hidden">
                  <button
                    type="button"
                    className="w-full sm:w-auto mt-2 sm:mt-0 px-6 py-2 bg-[#6741c6] text-white rounded-md flex items-center justify-center sm:justify-start hover:bg-[#5536a5] transition-colors duration-200"
                    onClick={() => scrollToSection(1)}
                  >
                    Next: Investment Terms
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </section>

            {/* Step 2: Investment Terms section */}
            <section 
              ref={sectionRefs.current[1] as React.RefObject<HTMLElement>}
              className="bg-slate-50 border border-slate-300 rounded-md p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mx-4 w-[calc(100%-32px)] sm:mx-0 sm:w-full">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#6741c6] text-white font-semibold mr-3 shadow-sm">
                  2
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Investment Terms</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4 ml-11">Set pricing and investment parameters</p>
              <hr className="border-t border-slate-200 mb-6" />
                
                <div className="space-y-6">
                  <div>
                    <label htmlFor="tokenPrice" className="block text-sm font-medium text-slate-900 mb-2">
                      Share Price
                    </label>
                    <div className="relative">
                      <div className="flex">
                        <input
                          id="tokenPrice"
                          name="tokenPrice"
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*\.?[0-9]*"
                          value={formState.tokenPrice}
                          onChange={handleInputChange}
                          placeholder="0.50"
                          className={`w-full px-3 py-2 pr-12 border ${
                            errors.tokenPrice 
                              ? 'border-red-500' 
                              : touchedFields.tokenPrice && validFields.tokenPrice
                                ? 'border-green-500'
                                : 'border-slate-300'
                          } rounded-l-md bg-white text-[#111827] placeholder:text-slate-400 placeholder:opacity-60
                          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                        <div className="flex flex-col border border-l-0 border-slate-300 rounded-r-md overflow-hidden">
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-300"
                            onClick={() => {
                              const currentValue = parseFloat(formState.tokenPrice) || 0;
                              const newValue = (currentValue + 0.01).toFixed(2);
                              setFormState(prev => ({ ...prev, tokenPrice: newValue }));
                              setTouchedFields(prev => ({ ...prev, tokenPrice: true }));
                              const errorMessage = validateField('tokenPrice', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, tokenPrice: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                            </svg>
                          </button>
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                            onClick={() => {
                              const currentValue = parseFloat(formState.tokenPrice) || 0;
                              const newValue = Math.max(0, (currentValue - 0.01)).toFixed(2);
                              setFormState(prev => ({ ...prev, tokenPrice: newValue }));
                              setTouchedFields(prev => ({ ...prev, tokenPrice: true }));
                              const errorMessage = validateField('tokenPrice', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, tokenPrice: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="absolute inset-y-0 right-14 flex items-center px-3 pointer-events-none">
                        {touchedFields.tokenPrice && validFields.tokenPrice && !errors.tokenPrice ? (
                          <div className="flex items-center">
                            <span className="text-slate-500 mr-2">USD</span>
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <span className="text-slate-500">USD</span>
                        )}
                      </div>
                    </div>
                    {errors.tokenPrice && (
                      <p className="mt-1 text-sm text-red-500">{errors.tokenPrice}</p>
                    )}
                    {formState.tokenPrice && !isNaN(Number(formState.tokenPrice)) && (
                      <p className="mt-1 text-xs text-slate-500">
                        Formatted: ${formatNumberWithCommas(formState.tokenPrice)} USD
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="minInvestment" className="block text-sm font-medium text-slate-900 mb-2">
                      Minimum Investment
                    </label>
                    <div className="relative">
                      <div className="flex">
                        <input
                          id="minInvestment"
                          name="minInvestment"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formState.minInvestment}
                          onChange={handleInputChange}
                          placeholder="100"
                          className={`w-full px-3 py-2 pr-16 border ${
                            errors.minInvestment 
                              ? 'border-red-500' 
                              : touchedFields.minInvestment && validFields.minInvestment
                                ? 'border-green-500'
                                : 'border-slate-300'
                          } rounded-l-md bg-white text-[#111827] placeholder:text-slate-400 placeholder:opacity-60
                          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                        <div className="flex flex-col border border-l-0 border-slate-300 rounded-r-md overflow-hidden">
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-300"
                            onClick={() => {
                              const currentValue = parseInt(formState.minInvestment) || 0;
                              const newValue = (currentValue + 1).toString();
                              setFormState(prev => ({ ...prev, minInvestment: newValue }));
                              setTouchedFields(prev => ({ ...prev, minInvestment: true }));
                              const errorMessage = validateField('minInvestment', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, minInvestment: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                            </svg>
                          </button>
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                            onClick={() => {
                              const currentValue = parseInt(formState.minInvestment) || 0;
                              const newValue = Math.max(0, (currentValue - 1)).toString();
                              setFormState(prev => ({ ...prev, minInvestment: newValue }));
                              setTouchedFields(prev => ({ ...prev, minInvestment: true }));
                              const errorMessage = validateField('minInvestment', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, minInvestment: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="absolute inset-y-0 right-14 flex items-center px-3 pointer-events-none">
                        {touchedFields.minInvestment && validFields.minInvestment && !errors.minInvestment ? (
                          <div className="flex items-center">
                            <span className="text-slate-500 mr-2">shares</span>
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <span className="text-slate-500">shares</span>
                        )}
                      </div>
                    </div>
                    {errors.minInvestment && (
                      <p className="mt-1 text-sm text-red-500">{errors.minInvestment}</p>
                    )}
                    {formState.minInvestment && !isNaN(Number(formState.minInvestment)) && (
                      <p className="mt-1 text-xs text-slate-500">
                        Formatted: {formatNumberWithCommas(formState.minInvestment)} shares
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="targetRaise" className="block text-sm font-medium text-slate-900 mb-2">
                      Target Raise
                    </label>
                    <div className="relative">
                      <div className="flex">
                        <input
                          id="targetRaise"
                          name="targetRaise"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formState.targetRaise}
                          onChange={handleInputChange}
                          placeholder="1000000"
                          className={`w-full px-3 py-2 pr-12 border ${
                            errors.targetRaise 
                              ? 'border-red-500' 
                              : touchedFields.targetRaise && validFields.targetRaise
                                ? 'border-green-500'
                                : 'border-slate-300'
                          } rounded-l-md bg-white text-[#111827] placeholder:text-slate-400 placeholder:opacity-60
                          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                        <div className="flex flex-col border border-l-0 border-slate-300 rounded-r-md overflow-hidden">
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-300"
                            onClick={() => {
                              const currentValue = parseInt(formState.targetRaise) || 0;
                              const newValue = (currentValue + 1000).toString();
                              setFormState(prev => ({ ...prev, targetRaise: newValue }));
                              setTouchedFields(prev => ({ ...prev, targetRaise: true }));
                              const errorMessage = validateField('targetRaise', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, targetRaise: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                            </svg>
                          </button>
                          <button 
                            type="button" 
                            className="flex items-center justify-center h-[50%] px-2 bg-slate-50 hover:bg-slate-100 transition-colors"
                            onClick={() => {
                              const currentValue = parseInt(formState.targetRaise) || 0;
                              const newValue = Math.max(0, (currentValue - 1000)).toString();
                              setFormState(prev => ({ ...prev, targetRaise: newValue }));
                              setTouchedFields(prev => ({ ...prev, targetRaise: true }));
                              const errorMessage = validateField('targetRaise', newValue);
                              if (!errorMessage) {
                                setValidFields(prev => ({ ...prev, targetRaise: true }));
                              }
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="absolute inset-y-0 right-14 flex items-center px-3 pointer-events-none">
                        {touchedFields.targetRaise && validFields.targetRaise && !errors.targetRaise ? (
                          <div className="flex items-center">
                            <span className="text-slate-500 mr-2">USD</span>
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <span className="text-slate-500">USD</span>
                        )}
                      </div>
                    </div>
                    {errors.targetRaise && (
                      <p className="mt-1 text-sm text-red-500">{errors.targetRaise}</p>
                    )}
                    {formState.targetRaise && !isNaN(Number(formState.targetRaise)) && (
                      <p className="mt-1 text-xs text-slate-500">
                        Formatted: ${formatNumberWithCommas(formState.targetRaise)} USD
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Section navigation buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-5 overflow-hidden">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-2 border border-slate-300 rounded-md flex items-center justify-center sm:justify-start hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => scrollToSection(0)}
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                    Back: Fund Details
                  </button>
                  <button
                    type="button"
                    className="w-full sm:w-auto mt-2 sm:mt-0 px-6 py-2 bg-[#6741c6] text-white rounded-md flex items-center justify-center sm:justify-start hover:bg-[#5536a5] transition-colors duration-200"
                    onClick={() => scrollToSection(2)}
                  >
                    Next: Offering Structure
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </section>
              
            {/* Step 3: Offering Structure section */}
            <section 
              ref={sectionRefs.current[2] as React.RefObject<HTMLElement>}
              className="bg-slate-50 border border-slate-300 rounded-md p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mx-4 w-[calc(100%-32px)] sm:mx-0 sm:w-full">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#6741c6] text-white font-semibold mr-3 shadow-sm">
                  3
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Offering Structure</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4 ml-11">Define who can invest and how your offering is structured</p>
              <hr className="border-t border-slate-200 mb-6" />
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-900 mb-3">
                    Offering Type
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { value: 'public', label: 'Public Offering', meta: 'Max investors: 250' },
                      { value: 'private', label: 'Private Placement', meta: 'Max investors: 100' },
                      { value: 'auction', label: 'Auction Offering', meta: 'Max investors: 500' }
                    ].map((option) => (
                      <div
                        key={option.value}
                        data-offering-type={option.value}
                        className={`p-4 border rounded-md cursor-pointer transition-all duration-300 hover:shadow-md ${
                          formState.offeringType === option.value
                            ? 'border-[#6741c6] bg-[#f8f5ff] shadow-md'
                            : 'border-slate-300 bg-white hover:border-[#6741c6]/30'
                        }`}
                        onClick={() => handleOfferingTypeChange(option.value as OfferingType)}
                      >
                        <div className="flex items-start mb-2">
                          <div
                            className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                              formState.offeringType === option.value
                                ? 'border-[#6741c6]'
                                : 'border-slate-300'
                            }`}
                          >
                            {formState.offeringType === option.value && (
                              <div className="w-2 h-2 rounded-full bg-[#6741c6]"></div>
                            )}
                          </div>
                          <span className="ml-2 font-medium">{option.label}</span>
                        </div>
                        <p className="text-xs text-slate-500 pl-6">{option.meta}</p>
                      </div>
                    ))}
                  </div>
                  {errors.offeringType && (
                    <p className="mt-1 text-sm text-red-500">{errors.offeringType}</p>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label htmlFor="accreditedOnly" className="text-sm font-medium text-slate-900 flex items-center cursor-pointer">
                      Accredited Investors Only
                      <span className="ml-2 text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full hover:bg-slate-200 transition-colors duration-200 cursor-help">?</span>
                    </label>
                    <button
                      id="accreditedOnly"
                      type="button"
                      onClick={handleToggleChange('accreditedOnly')}
                      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 ${
                        formState.accreditedOnly ? 'bg-[#6741c6]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block w-5 h-5 transform bg-white rounded-full transition-transform duration-300 ${
                          formState.accreditedOnly ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* Progressive disclosure - additional info appears when option is enabled */}
                  <div 
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      formState.accreditedOnly 
                        ? 'max-h-48 opacity-100' 
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="bg-[#f8f5ff] border border-[#6741c6]/30 p-3 rounded-md">
                      <div className="flex items-start">
                        <div className="mt-0.5">
                          <svg className="w-4 h-4 text-[#6741c6] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-slate-700 mb-1">
                            <span className="font-medium">Accredited Investor Requirements:</span> Investors must meet SEC qualifications.
                          </p>
                          <p className="text-xs text-slate-600">
                            Each investor will need to verify their accredited status before participating in this offering.
                          </p>
                          <div className="mt-2 flex justify-between items-center">
                            <button 
                              type="button"
                              className="text-xs text-[#6741c6] hover:text-[#5536a5] font-medium transition-colors duration-200 flex items-center"
                              onClick={() => {
                                notifyInfo(
                                  'KYC/AML verification will be enabled automatically for accredited investors', 
                                  NotificationPriority.LOW, 
                                  { autoClose: 5000 }
                                );
                              }}
                            >
                              <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                              </svg>
                              Learn more about requirements
                            </button>
                            <div className="animate-fadeIn">
                              <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded-full text-xs">KYC Required</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Section navigation buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-5 overflow-hidden">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-2 border border-slate-300 rounded-md flex items-center justify-center sm:justify-start hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => scrollToSection(1)}
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                    Back: Investment Terms
                  </button>
                  <button
                    type="button"
                    className="w-full sm:w-auto mt-2 sm:mt-0 px-6 py-2 bg-[#6741c6] text-white rounded-md flex items-center justify-center sm:justify-start hover:bg-[#5536a5] transition-colors duration-200"
                    onClick={() => scrollToSection(3)}
                  >
                    Next: Fund Timeline
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </section>
              
            {/* Step 4: Fund Timeline section */}
            <section 
              ref={sectionRefs.current[3] as React.RefObject<HTMLElement>}
              className="bg-slate-50 border border-slate-300 rounded-md p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mx-4 w-[calc(100%-32px)] sm:mx-0 sm:w-full">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#6741c6] text-white font-semibold mr-3 shadow-sm">
                  4
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Fund Timeline</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4 ml-11">Set launch and closing dates for your offering</p>
              <hr className="border-t border-slate-200 mb-6" />
                
                <div className="space-y-6">
                  <div>
                    <label htmlFor="launchDate" className="block text-sm font-medium text-slate-900 mb-2">
                      Launch Date
                    </label>
                    <div
                      className={`flex items-center px-3 py-2 border ${
                        errors.launchDate 
                          ? 'border-red-500' 
                          : touchedFields.launchDate && validFields.launchDate
                            ? 'border-green-500'
                            : 'border-slate-300'
                      } rounded-md cursor-pointer bg-white text-[#111827]`}
                      onClick={() => handleDateClick(launchDateInputRef)}
                    >
                      <input
                        ref={launchDateInputRef}
                        type="date"
                        id="launchDate"
                        className="opacity-0 absolute"
                        value={formState.launchDate || ''}
                        onChange={handleDateChange('launchDate')}
                      />
                      <span className="flex-grow">
                        {formState.launchDate || 'Select launch date'}
                      </span>
                      
                      {touchedFields.launchDate && validFields.launchDate && !errors.launchDate ? (
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          ></path>
                        </svg>
                      )}
                    </div>
                    {errors.launchDate && (
                      <p className="mt-1 text-sm text-red-500">{errors.launchDate}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="closingDate" className="block text-sm font-medium text-slate-900 mb-2">
                      Closing Date
                    </label>
                    <div
                      className={`flex items-center px-3 py-2 border ${
                        errors.closingDate 
                          ? 'border-red-500' 
                          : touchedFields.closingDate && validFields.closingDate
                            ? 'border-green-500'
                            : 'border-slate-300'
                      } rounded-md cursor-pointer bg-white text-[#111827]`}
                      onClick={() => handleDateClick(closingDateInputRef)}
                    >
                      <input
                        ref={closingDateInputRef}
                        type="date"
                        id="closingDate"
                        className="opacity-0 absolute"
                        value={formState.closingDate || ''}
                        onChange={handleDateChange('closingDate')}
                      />
                      <span className="flex-grow">
                        {formState.closingDate || 'Select closing date'}
                      </span>
                      
                      {touchedFields.closingDate && validFields.closingDate && !errors.closingDate ? (
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          ></path>
                        </svg>
                      )}
                    </div>
                    {errors.closingDate && (
                      <p className="mt-1 text-sm text-red-500">{errors.closingDate}</p>
                    )}
                  </div>
                </div>
                
                {/* Section navigation buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-5 overflow-hidden">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-2 border border-slate-300 rounded-md flex items-center justify-center sm:justify-start hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => scrollToSection(2)}
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                    Back: Offering Structure
                  </button>
                  <button
                    type="button"
                    className="w-full sm:w-auto mt-2 sm:mt-0 px-6 py-2 bg-[#6741c6] text-white rounded-md flex items-center justify-center sm:justify-start hover:bg-[#5536a5] transition-colors duration-200"
                    onClick={() => scrollToSection(4)}
                  >
                    Next: Fund Agreement
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </section>
              
            {/* Step 5: Fund Agreement section */}
            <section 
              ref={sectionRefs.current[4] as React.RefObject<HTMLElement>}
              className="bg-slate-50 border border-slate-300 rounded-md p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden mx-4 w-[calc(100%-32px)] sm:mx-0 sm:w-full">
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#6741c6] text-white font-semibold mr-3 shadow-sm">
                  5
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Fund Agreement</h2>
              </div>
              <p className="text-sm text-slate-600 mb-4 ml-11">Upload legal documents required for your offering</p>
              <hr className="border-t border-slate-200 mb-6" />
                
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Upload Legal Offering Document
                  </label>
                  <div className="border border-dashed border-slate-300 rounded-md p-6 flex flex-col items-center">
                    {formState.agreementDoc ? (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                          <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                          <span className="ml-2 text-sm truncate">
                            {formState.agreementDoc.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="text-red-500"
                          onClick={() => handleFileChange('agreementDoc')(null)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <svg className="w-12 h-12 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                        </svg>
                        <p className="text-sm text-slate-500 mb-2">Drag and drop your PDF file here, or</p>
                        <input
                          type="file"
                          id="agreementDoc"
                          className="hidden"
                          accept=".pdf"
                          onChange={(e) => handleFileChange('agreementDoc')(e.target.files?.[0] || null)}
                        />
                        <label
                          htmlFor="agreementDoc"
                          className="px-4 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-900 cursor-pointer hover:bg-slate-50"
                        >
                          Browse Files
                        </label>
                      </>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">PDF only</p>
                  {errors.agreementDoc && (
                    <p className="mt-1 text-sm text-red-500">{errors.agreementDoc}</p>
                  )}
                </div>
                
                {/* Section navigation buttons */}
                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-5 overflow-hidden">
                  <button
                    type="button"
                    className="w-full sm:w-auto px-6 py-2 border border-slate-300 rounded-md flex items-center justify-center sm:justify-start hover:bg-slate-50 transition-colors duration-200"
                    onClick={() => scrollToSection(3)}
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                    Back: Fund Timeline
                  </button>
                  <button
                    type="submit"
                    form="create-fund-form"
                    className="w-full sm:w-auto mt-2 sm:mt-0 px-6 py-2 bg-[#6741c6] text-white rounded-md flex items-center justify-center sm:justify-start hover:bg-[#5536a5] transition-colors duration-200"
                  >
                    Save Fund
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </button>
                </div>
              </section>
          </div>
          
          {/* Progress indicator */}
          <div className="fixed bottom-20 right-10 hidden lg:flex flex-col items-end">
            <div className="bg-white shadow-lg rounded-lg p-4 w-72 border border-slate-200">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Fund Creation Progress</h3>
              <div className="space-y-2">
                {['Fund Details', 'Investment Terms', 'Offering Structure', 'Fund Timeline', 'Fund Agreement'].map((step, index) => (
                  <div 
                    key={index} 
                    className="flex items-center cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors duration-200"
                    onClick={() => scrollToSection(index)}
                  >
                    <div 
                      className={`w-6 h-6 flex items-center justify-center rounded-full mr-2 transition-colors duration-300 ${
                        index === activeSection 
                          ? 'bg-[#6741c6] text-white' 
                          : index < activeSection
                            ? 'bg-[#9d7ae0] text-white'
                            : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {index < activeSection ? '✓' : index + 1}
                    </div>
                    <span className={`text-sm ${index === activeSection ? 'font-medium' : ''}`}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Footer text */}
          <div className="mt-8 mb-16 p-4 bg-slate-50 border border-slate-200 rounded-md">
            <p className="text-sm text-slate-600">
              By tokenizing with Equitee, you unlock global capital, streamline compliance, and offer fractional access to sophisticated investors.
            </p>
          </div>
        </form>
      </main>
    </DesktopLayout>
  );
};

export default CreateFund;